import { CurrencyPipe, DatePipe, NgOptimizedImage } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { BookingReason } from '../../../domain/BookingReason'
import { Event } from '../../../domain/Event'
import { CurrencyUnitPipe } from '../../../pipes/currency-unit.pipe'
import { ReasonTimePipe } from '../../../pipes/reason-time.pipe'
import { environment } from '../../../../environments/environment'
import { richTextPlainText } from '../../../utilities/rich-text'
import { QuillModule } from 'ngx-quill'

type VenueStorefrontModalContent = {
    kind: 'event'
    event: Event
    date: Date
    currencyCode: string | null
} | {
    kind: 'reason'
    reason: BookingReason
    date: Date
}

@Component({
    selector: 'app-venue-storefront-event-modal',
    standalone: true,
    imports: [CurrencyUnitPipe, DatePipe, NgOptimizedImage, ReasonTimePipe, QuillModule],
    providers: [CurrencyPipe],
    templateUrl: './venue-storefront-event-modal.component.html',
    styleUrls: ['./venue-storefront-event-modal.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontEventModalComponent {
    private activeModal = inject(NgbActiveModal)

    protected content = signal<VenueStorefrontModalContent | null>(null)
    protected bookingSelectionEnabled = signal(false)
    protected description = computed(() => {
        const content = this.content()
        const description = content?.kind === 'event' ? content.event.description : content?.reason.description ?? null
        return richTextPlainText(description) ? description : null
    })
    protected imageUrl = computed(() => {
        const content = this.content()
        if (content === null) {
            return null
        }
        const imageId = content.kind === 'event'
            ? content.event.imageId
            : content.reason.imageId
        const directory = content.kind === 'event' ? 'event-images' : 'reason-images'
        return imageId
            ? `${environment.cdnUrl}/582x240/${directory}/${imageId}`
            : null
    })
    protected startDateTime = computed(() => {
        const content = this.content()
        return content?.kind === 'event' && content.event.recurrence.startTime
            ? content.date
            : null
    })
    protected endDateTime = computed(() => {
        const content = this.content()
        return content?.kind === 'event' && content.event.recurrence.endTime
            ? content.event.recurrence.endTime.dateTimeOnDate(content.date)
            : null
    })
    protected hasCharges = computed(() => {
        const content = this.content()
        if (content?.kind !== 'event') {
            return false
        }
        return [
            content.event.depositAmount,
            content.event.depositFlatFeeAmount,
            content.event.cancellationChargeAmount,
            content.event.cancellationChargePartyAmount,
        ].some(amount => (amount ?? 0) > 0)
    })

    configure(event: Event, nextDateTime: Date, currencyCode: string | null) {
        this.content.set({
            kind: 'event',
            event,
            date: nextDateTime,
            currencyCode,
        })
    }

    configureReason(reason: BookingReason, date: Date) {
        this.content.set({
            kind: 'reason',
            reason,
            date,
        })
    }

    enableBookingSelection() {
        this.bookingSelectionEnabled.set(true)
    }

    protected dismiss() {
        this.activeModal.dismiss()
    }

    protected selectAndContinue() {
        this.activeModal.close()
    }
}
